// @ts-strict-ignore
import React, {
  ComponentProps,
  ForwardedRef,
  forwardRef,
  memo,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { getShortName } from 'services/form'
import { FormValueInput, FormValueMeta, Nullable } from 'types'
import { Autocomplete, AutocompleteInputChangeReason, CircularProgress, MenuItem } from '@mui/material'
import { useLogger } from 'hooks/logger'
import { useListBoxScrollListener } from 'hooks/search'
import { makeStyles } from 'tss-react/mui'
import Suspense from 'components/Suspense'
import TextField from '../TextField'
import { IAutoCompleteOption } from './types'

interface IAutoCompleteTextFieldProps<
  P extends IAutoCompleteOption,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
> extends Omit<ComponentProps<typeof Autocomplete<P, false, DisableClearable, FreeSolo>>, 'renderInput'> {
  maxOptions?: number
  input: FormValueInput<Nullable<P | string>>
  meta?: FormValueMeta
  label?: string
  helperText?: React.ReactNode
  noOptionsText?: React.ReactNode
  fullWidth?: boolean
  loading?: boolean
  placeholder?: string
  loadMore?: (() => void) | null
}

const useStyles = makeStyles()(theme => ({
  textField: {
    '& > .MuiInputBase-root': {
      // TODO: figure out why this override is needed, and why this field can't just have the same height as other TextFields on its own
      padding: '8px 64px 7px 8px !important',
    },
  },
  errorText: {
    color: 'red',
  },
  chevron: {
    transition: 'transform 0.4s ease',
    marginRight: theme.spacing(-1),
  },
  end: {
    marginTop: -3,
  },
  loader: {
    display: 'block',
    position: 'absolute',
    top: 9,
    right: 46,
    pointerEvents: 'none',
  },
}))

function AutoCompleteTextField<
  P extends IAutoCompleteOption,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>(props: IAutoCompleteTextFieldProps<P, DisableClearable, FreeSolo>, ref: ForwardedRef<HTMLInputElement>) {
  const { classes } = useStyles()
  const {
    input,
    meta,
    label,
    helperText,
    noOptionsText,
    fullWidth = true,
    maxOptions,
    loading = false,
    options = [],
    freeSolo,
    loadMore,
    onKeyDown,
    onKeyUp,
    placeholder,
    ...restProps
  } = props
  const [inputValue, setInputValue] = useState(typeof input.value === 'string' ? input.value : input.value?.label || '')

  const logger = useLogger('AutoCompleteTextField', input?.name)

  const preparedOptions = useMemo<readonly P[]>(() => {
    let structuredOptions = options

    if (maxOptions > 0 && Array.isArray(options)) {
      // TODO: also show some text at the end of the list saying some options are hidden
      structuredOptions = structuredOptions.slice(0, Math.min(options.length, maxOptions)) as readonly P[]
    }

    return structuredOptions
  }, [options, maxOptions])

  const onChange = useCallback((event, value, reason, details) => {
    // Prevent the form from being submitted when pressing enter
    event?.preventDefault()
    event?.stopPropagation()

    logger.debug('onChange', value)
    let newValue = value

    if (typeof newValue === 'string' && props.autoSelect) {
      const matchingOption = options.find(o => typeof o === 'string' ? false : o.label === value)
      if (matchingOption) newValue = matchingOption
    }

    input?.onChange(newValue)
    if (typeof props.onChange === 'function') props.onChange(event, newValue, reason, details)
  }, [input?.onChange, options, props.autoSelect])

  const onInputChange = useCallback((event: SyntheticEvent, newValue: string, reason: AutocompleteInputChangeReason) => {
    // Prevent the form from being submitted when pressing enter
    event?.preventDefault()
    event?.stopPropagation()
    logger.debug('onInputChange', newValue, reason)
    if (newValue === 'undefined' && reason === 'reset') return

    setInputValue(newValue)
    if (typeof props.onInputChange === 'function') props.onInputChange(event, newValue, reason)
    if (freeSolo) {
      onChange(event, newValue, reason, null)
    }
  }, [onChange, props.onInputChange, freeSolo])

  // TODO: show "no results" message in dropdown, customized depending on enforceSuggestionSelection
  // Also allow overriding it via props

  logger.debug('options', preparedOptions, maxOptions)

  // TODO: figure out how to remove the any cast
  const actualValue = useMemo<any>(() => input?.value || null, [input?.value])

  const isOptionEqualToValue = useCallback((option: P, value: P) => {
    return option?.value === value?.value || option === value
  }, [])

  const handleScroll = useListBoxScrollListener(loadMore)

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
    }

    onKeyDown?.(e)
  }, [onKeyDown])

  const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
    }

    onKeyUp?.(e)
  }, [onKeyUp])

  logger.debug('value', actualValue)

  return (
    <Suspense
      // IMPORTANT NOTE: this suspense boundary fixes a re-render stackoverflow error caused by <Autocomplete> (although the issue itself is unclear)
      fallback={(
        <TextField
          fullWidth={fullWidth}
          label={label}
          disabled
        />
      )}
    >
      <Autocomplete<IAutoCompleteOption, false, DisableClearable, FreeSolo>
        getOptionLabel={option => typeof option === 'string' ? option : option.label}
        isOptionEqualToValue={isOptionEqualToValue}
        data-cy={`autocomplete-${getShortName(input.name)}`}
        {...restProps}
        ref={ref}
        fullWidth={fullWidth}
        value={actualValue}
        onChange={onChange}
        onInputChange={onInputChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        inputValue={inputValue}
        noOptionsText={noOptionsText || 'No results'}
        options={preparedOptions}
        loading={loading}
        ListboxProps={{
          onScroll: handleScroll,
        }}
        renderOption={(optionProps, option) => (
          <MenuItem
            {...optionProps}
            key={typeof option === 'string' ? option : (option.value || option.label)}
            data-cy={`autocomplete-option-${typeof option === 'string' ? option : (option.value || option.label)}`}
          >
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {option.label}
            </div>
          </MenuItem>
        )}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
            className={classes.textField}
            name={input.name}
            label={label}
            error={Boolean(meta?.touched && meta?.error)}
            helperText={
              (meta?.touched && meta?.error && (
                <span className={classes.errorText} data-cy={`autocomplete-${getShortName(input.name)}-helper-text`}>{meta?.error}</span>)
              ) || helperText
            }
            onBlur={input.onBlur}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <div className={classes.end}>
                  {loading ? <CircularProgress className={classes.loader} color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </div>
              ),
            }}
          />
        )}
      />
    </Suspense>
  )
}

export default memo(forwardRef(AutoCompleteTextField))
